import React, { useEffect, useState } from 'react';
import { useGame } from '../context/GameContext';
import { CURSES, CURSE_LIST, stackedTokenBonus, tallyCurseStack, MAX_CURSE_STACK } from '../game/curses';
import { fetchCurseStack } from '../game/profile';
import { Skull, Zap, Layers } from 'lucide-react';

// v3.10 STACKED CURSES: displays the current stack + lets the player pile another one on for growing rewards.
export default function CurseSelect({ runCode, onDone }) {
  const { curseThisRun, profile } = useGame();
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [stack, setStack] = useState([]);
  const [refreshing, setRefreshing] = useState(true);

  const ownedPremium = (profile?.owned_curses || []).filter(id => CURSES[id]);
  const options = [...CURSE_LIST, ...ownedPremium];

  const loadStack = async () => {
    setRefreshing(true);
    const r = await fetchCurseStack(runCode);
    setStack(r?.curses || []);
    setRefreshing(false);
  };
  useEffect(() => { loadStack(); }, [runCode]);

  const commit = async () => {
    if (!selected) return;
    setBusy(true);
    const r = await curseThisRun(runCode, selected);
    setBusy(false);
    if (r) {
      setSelected(null);
      await loadStack();
      if (onDone) onDone(r);
    }
  };

  // Next-tier reward preview
  const nextIndex = stack.length;
  const nextMult = 1 + nextIndex * 0.5;
  const previewBonus = selected
    ? Math.floor((CURSES[selected]?.tokenBonus || 0) * nextMult)
    : 0;
  const currentStackBonus = stackedTokenBonus(stack.map(s => s.curse_id));
  // v3.18 STACKED CURSES: tally same-curse counts + enforce hard 100 cap.
  const stackTally = tallyCurseStack(stack.map(s => s.curse_id));
  const stackFull = stack.length >= MAX_CURSE_STACK;

  return (
    <div className="brutal-border-pink p-6 bg-[#0a0a0a]" data-testid="curse-select-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[color:var(--pink)]">
          <Skull size={14}/> CURSE THIS RUN CODE
        </div>
        <div className="flex items-center gap-3">
          {ownedPremium.length > 0 && (
            <div className="text-[10px] uppercase text-[color:var(--pink)]">OWNED PREMIUM: {ownedPremium.length}</div>
          )}
          <div className="text-[10px] uppercase text-[color:var(--text-dim)] flex items-center gap-1" data-testid="curse-stack-badge">
            <Layers size={12}/> STACK: <span className="text-white font-bold">{stack.length}</span>/<span className="text-[color:var(--text-dim)]">{MAX_CURSE_STACK}</span>
          </div>
        </div>
      </div>
      <div className="mt-1 text-xs text-[color:var(--text-dim)]">
        curses <b>stack</b>. each additional curse compounds modifiers AND grows the token payout the next player earns for surviving <span className="text-white font-bold">{runCode}</span>.
      </div>

      {/* v3.10: existing stack visualiser */}
      {stack.length > 0 && (
        <div className="mt-4" data-testid="curse-stack-display">
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-dim)] mb-1">CURRENT STACK · +{currentStackBonus} TOKENS ON WIN</div>
          <div className="flex flex-wrap gap-2">
            {stack.map((s, i) => {
              const c = CURSES[s.curse_id];
              if (!c) return null;
              const rowMult = 1 + i * 0.5;
              return (
                <div key={i}
                  className="brutal-border-dim p-2 flex items-center gap-2"
                  style={{ borderColor: c.color, boxShadow: `2px 2px 0 ${c.color}` }}
                  data-testid={`curse-stack-item-${i}`}
                  title={`Layer ${i+1} · ${rowMult}× bonus`}
                >
                  <span className="text-lg">{c.art}</span>
                  <div>
                    <div className="font-display font-black uppercase text-xs" style={{ color: c.color }}>{c.name}</div>
                    <div className="text-[9px] uppercase tracking-wide text-white/70">
                      LAYER {i+1} · <span className="text-[color:var(--pink)] font-bold">{rowMult}×</span> · +{Math.floor((c.tokenBonus || 0) * rowMult)}T
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-2">
        {options.map(cid => {
          const c = CURSES[cid];
          if (!c) return null;
          const active = selected === cid;
          // v3.18: same curse can be stacked multiple times — show the current count instead of blocking.
          const count = stackTally.get(cid) || 0;
          const disabled = stackFull;
          return (
            <button
              key={cid}
              onClick={() => !disabled && setSelected(cid)}
              className={`text-left p-3 border-2 transition-all ${active ? 'bg-[#160a10]' : disabled ? 'bg-[#050505] opacity-40' : 'bg-[#080808] hover:bg-[#0e0e0e]'}`}
              style={{
                borderColor: active ? c.color : disabled ? '#333' : 'var(--muted)',
                boxShadow: active ? `4px 4px 0 ${c.color}` : 'none',
                cursor: disabled ? 'not-allowed' : 'pointer',
              }}
              disabled={disabled}
              data-testid={`curse-option-${cid}`}
            >
              <div className="flex items-center gap-2">
                <span className="text-xl">{c.art}</span>
                <span className="font-display font-black uppercase text-sm" style={{color: c.color}}>{c.name}</span>
                {c.premium && <span className="text-[8px] uppercase font-bold text-[color:var(--pink)]">★ PREMIUM</span>}
                {count > 0 && (
                  <span
                    className="text-[10px] uppercase font-black px-1.5 py-0.5 ml-auto"
                    style={{ border: `1px solid ${c.color}`, color: c.color, background: `${c.color}22` }}
                    data-testid={`curse-option-count-${cid}`}
                  >×{count}</span>
                )}
              </div>
              <div className="text-[10px] text-[color:var(--text-dim)] mt-1">{c.desc}</div>
              <div className="text-[10px] text-[color:var(--text-dim)] mt-1">
                Base bonus: <span className="text-white font-bold">+{c.tokenBonus || 0}T</span>
              </div>
              {c.tagline && <div className="text-[9px] italic text-[color:var(--text-dim)] mt-1">&ldquo;{c.tagline}&rdquo;</div>}
            </button>
          );
        })}
      </div>

      <div className="mt-4 flex items-center justify-between gap-2 flex-wrap">
        {selected ? (
          <div className="text-xs uppercase tracking-widest text-[color:var(--neon)]" data-testid="curse-preview-line">
            NEXT LAYER {nextIndex + 1} · <b>{nextMult}×</b> · +{previewBonus} TOKENS
          </div>
        ) : (
          <div className="text-xs text-[color:var(--text-dim)]">
            {stackFull ? `STACK FULL (${MAX_CURSE_STACK}/${MAX_CURSE_STACK}) — NO MORE CURSES CAN BE PLANTED.` :
              refreshing ? 'LOADING STACK...' : stack.length > 0 ? 'PLANT ANOTHER LAYER FOR MORE PAIN.' : 'PICK YOUR FIRST CURSE.'}
          </div>
        )}
        <button className="brutal-btn-pink brutal-btn text-sm" onClick={commit} disabled={!selected || busy || stackFull} data-testid="curse-commit-button">
          <Zap size={14}/> {busy ? 'PLANTING...' : stackFull ? 'STACK FULL' : stack.length > 0 ? 'STACK IT' : 'PLANT CURSE'}
        </button>
      </div>
    </div>
  );
}
